import { defaultOrgConfig } from '@mesa/core/config';
import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, useEffect } from 'react';
import { toast } from 'sonner';

import { MarkdownEditor } from '@/components/editor/markdown-editor';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { useZero } from '@/lib/zero';
import { useCurrentOrg } from '@/services/org';

export const Route = createFileRoute('/_authed/_layout/$owner/settings/pull-requests')({
  component: PullRequestSettingsComponent,
});

function PullRequestSettingsComponent() {
  const z = useZero();
  const { owner } = Route.useParams();
  const { currentOrg } = useCurrentOrg();

  const [configRecord] = useZeroQuery(z.query.OrgConfigTable.where('owner', owner).one());

  // Pulls config
  const pullsConfig = useMemo(() => {
    return (configRecord?.config ?? defaultOrgConfig).pulls;
  }, [configRecord?.config]);

  // Local state for pulls description template editing
  const [descriptionTemplate, setDescriptionTemplate] = useState<string>(pullsConfig.description.template);

  // Sync template state when pullsConfig changes externally
  useEffect(() => {
    setDescriptionTemplate(pullsConfig.description.template);
  }, [pullsConfig.description.template]);

  const updatePulls = async (updates: Partial<typeof pullsConfig>): Promise<void> => {
    try {
      await z.mutate.OrgConfigTable.upsert({
        owner: currentOrg.account.login,
        installationId: currentOrg.id,
        config: {
          ...(configRecord?.config ?? defaultOrgConfig),
          pulls: {
            ...pullsConfig,
            ...updates,
          },
        },
      });
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update setting. Please try again.');
    }
  };

  return (
    <div className="size-full">
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl lg:max-w-3xl mx-auto space-y-8">
        {/* Automatic Description Settings */}
        <div className="flex flex-col gap-2">
          <Label className="text-lg font-medium">Automatic Descriptions</Label>
          <RadioGroup
            value={pullsConfig.description.autoGenerate ? 'enabled' : 'disabled'}
            onValueChange={(value) =>
              updatePulls({ description: { ...pullsConfig.description, autoGenerate: value === 'enabled' } })
            }
          >
            <div className="flex flex-col gap-2">
              {[
                {
                  key: 'enabled',
                  label: 'Enabled',
                  description:
                    'Mesa will generate a description when a PR is opened or published from draft. If a description already exists, Mesa will not override it.',
                },
                {
                  key: 'disabled',
                  label: 'Disabled',
                  description:
                    'Mesa will not automatically generate a description. You can manually generate one by tagging `@mesa-dot-dev describe` in your pull request.',
                },
              ].map((opt) => {
                const isSelected = pullsConfig.description.autoGenerate === (opt.key === 'enabled');
                return (
                  <Label
                    key={opt.key}
                    htmlFor={opt.key}
                    className={cn(
                      'radio-tile',
                      isSelected
                        ? opt.key === 'enabled'
                          ? 'radio-tile-selected-emerald'
                          : 'radio-tile-selected-orange'
                        : 'radio-tile-unselected'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value={opt.key} id={opt.key} className="sr-only" />
                      <span className="font-medium text-base">{opt.label}</span>
                    </div>
                    <span className="text-sm text-muted-foreground font-normal">{opt.description}</span>
                  </Label>
                );
              })}
            </div>
          </RadioGroup>

          {pullsConfig.description.autoGenerate && (
            <div className="flex flex-col gap-2 mt-4">
              <Label htmlFor="description-template" className="text-base font-medium">
                Description Template
              </Label>
              <MarkdownEditor
                owner={owner}
                repo=""
                key="template-editor"
                value={descriptionTemplate}
                onBlur={({ editor }) => {
                  const md = editor.storage.markdown.getMarkdown();
                  setDescriptionTemplate(md);
                  updatePulls({ description: { ...pullsConfig.description, template: md } });
                }}
                placeholder="Add a pull request description template..."
                className="inset-shadow-sm bg-stone-50/75 rounded-lg p-2 min-h-[300px]"
                showBubbleMenu
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
